<template>
  <Card :customClasses="customClasses">
    <div class="flex">
      <Avatar :name="name" :image-url="avatarPhotoUrl" size="xl" class="mr-16" />
      <div>
        <span class="flex flex-wrap mb-8 items-center">
          <h2 class="text-h4 text-ellipsis overflow-hidden whitespace-nowrap mr-8">{{ name }}</h2>
          <poppins-badge
            v-if="matchStrengthDetails && isApplicantMatchScoreEnabled"
            :badgeContent="matchStrengthDetails.text"
            :variant="matchStrengthDetails.variant"
          />
        </span>
        <p v-if="distance" class="text-body mb-4">
          {{ distanceText }}
        </p>
        <p class="text-body mb-4">{{ rateText }}</p>
        <p v-if="lastLogin" class="text-body mb-4">Last login: {{ lastLogin }}</p>
        <p v-if="createdAt" class="text-caption text-grey-clickable">Applied {{ daysAgoAppliedDisplayText }}</p>
      </div>
    </div>
  </Card>
</template>

<script lang="ts">
import Card from '@/components/card/Card.vue';
import Avatar from '@/components/avatar/Avatar.vue';
import PoppinsBadge from '@/components/badge/Badge.vue';
import { isOn } from '@/lib/growthbook';

export default {
  name: 'ApplicantCard',
  components: {
    Card,
    Avatar,
    PoppinsBadge,
  },
  props: {
    careType: {
      type: String,
      default: 'BABYSITTER',
    },
    name: {
      type: String,
      default: '',
    },
    distance: {
      type: Number,
      required: true,
    },
    location: {
      type: Object,
      required: true,
    },
    minRate: {
      type: Number,
      default: null,
    },
    maxRate: {
      type: Number,
      default: null,
    },
    lastLogin: {
      type: String,
      default: '',
    },
    createdAt: {
      type: String,
      default: '',
    },
    avatarPhotoUrl: {
      type: String,
      default: '',
    },
    customClasses: {
      type: String,
      default: '',
      required: false,
    },
    matchStrength: {
      type: String,
      default: '',
      required: false,
    },
  },
  computed: {
    isBasic(): boolean {
      return this.$store.getters['account/getAccount']?.isBasic() || false;
    },
    isApplicantMatchScoreEnabled(): boolean {
      return isOn('sc-applications-match-score');
    },
    matchStrengthDetails(): { text: string; variant: string } | null {
      if (this.isBasic) return null;
      switch (this.matchStrength) {
        case 'STRONG':
          return { text: 'Strong fit', variant: 'strong' };
        case 'MODERATE':
          return { text: 'Moderate fit', variant: 'moderate' };
        case 'PARTIAL':
          return { text: 'Partial fit', variant: 'subdued' };
        default:
          return null;
      }
    },
    sanitizedDistance(): string {
      return this.distance.toFixed(1);
    },
    distanceText(): string {
      let dt = `${this.location.cityname}, ${this.location.stateabbr} • `;
      if (this.distance < 1) {
        dt += '< 1 mile';
      } else {
        dt += `${this.sanitizedDistance} miles`;
      }
      return dt;
    },
    computedMin(): number {
      // https://git.sittercity.com/sittercity/sittercity/blob/master/app/presenters/jobs/pay_rate_info.rb#L38
      return Math.floor(this.minRate / 100);
    },
    computedMax(): number {
      // https://git.sittercity.com/sittercity/sittercity/blob/master/app/presenters/jobs/pay_rate_info.rb#L39
      return Math.ceil(this.maxRate / 100);
    },
    rateText(): string {
      if (this.computedMin > 0 && this.computedMax > 0) {
        return `$${this.computedMin}–${this.computedMax}/hr`;
      }
      if (this.computedMax > 0) {
        if (this.careType === 'PET_CARE') {
          return `$${this.computedMax}/visit`;
        }
        return `$${this.computedMax}/hr`;
      }
      if (this.computedMin > 0) {
        return `$${this.computedMin}/hr`;
      }
      return '';
    },
    daysAgoApplied(): number {
      const createdDate = new Date(this.createdAt);
      const currentDate = new Date();
      const timeDifference = currentDate.getTime() - createdDate.getTime();
      return Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    },
    daysAgoAppliedDisplayText(): string {
      const daysAgo = this.daysAgoApplied;
      if (daysAgo === 0) {
        return 'today';
      }
      if (daysAgo === 1) {
        return 'yesterday';
      }
      if (daysAgo > 1 && daysAgo < 7) {
        return `${daysAgo} days ago`;
      }
      if (daysAgo >= 7 && daysAgo < 30) {
        const weeksAgo = Math.floor(daysAgo / 7);
        return weeksAgo === 1 ? '1 week ago' : `${weeksAgo} weeks ago`;
      }
      if (daysAgo >= 30) {
        return 'over a month ago';
      }
      return '';
    },
  },
};
</script>
